/* ==========================================================================
   BEHIND - FIND YOUR SIZE
   Behaviour for the five step size adviser. Kept separate from the Prestige
   core files. Loaded from layout/theme.liquid on product pages.

   Flow: basic details, stomach shape, torso shape, thigh shape, advice.

   Only two things decide the advice: the size the customer says they normally
   wear and the fit of the product (true to size, runs small, runs large).
   Height, weight, age and the three body shapes are collected and remembered
   so they can feed a smarter model later, but they change nothing today.

   The answers live in localStorage, so the product page keeps showing the last
   advice until the customer starts over.
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'behind-find-your-size';
  var CANONICAL_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  var openModal = null;
  var previousOverflow = '';

  function defaultState() {
    return {
      completed: false,
      fit: 'ideal',
      brandSize: '',
      height: '',
      heightUnit: '',
      weight: '',
      weightUnit: '',
      age: '',
      stomach: '',
      torso: '',
      thigh: ''
    };
  }

  function readState() {
    var state = defaultState();

    try {
      var stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');

      Object.keys(state).forEach(function (key) {
        if (typeof stored[key] !== 'undefined' && stored[key] !== null) {
          state[key] = stored[key];
        }
      });
    } catch (error) {
      /* Storage can be unavailable. The adviser then simply starts fresh. */
    }

    return state;
  }

  function writeState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      /* Nothing to do, the flow keeps working for this page view. */
    }
  }

  function canonicalIndex(name) {
    var cleaned = String(name || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

    if (cleaned === '2XL') {
      cleaned = 'XXL';
    }

    if (cleaned === '3XL') {
      cleaned = 'XXXL';
    }

    return CANONICAL_SIZES.indexOf(cleaned);
  }

  function clamp(value, max) {
    if (value < 0) {
      return 0;
    }

    if (value > max) {
      return max;
    }

    return value;
  }

  function showModal(modal) {
    if (!modal || modal === openModal) {
      return;
    }

    hideModal();

    /* Moved to the end of the body so no stacking context can cover it. */
    if (modal.parentElement !== document.body) {
      var duplicates = document.querySelectorAll('[id="' + modal.id + '"]');

      Array.prototype.forEach.call(duplicates, function (element) {
        if (element !== modal && element.parentElement === document.body) {
          element.parentElement.removeChild(element);
        }
      });

      document.body.appendChild(modal);
    }

    modal.hidden = false;
    openModal = modal;
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  function hideModal() {
    if (!openModal) {
      return;
    }

    openModal.hidden = true;
    openModal = null;
    document.documentElement.style.overflow = previousOverflow;
  }

  if (window.customElements && !window.customElements.get('find-your-size')) {
    var FindYourSize = function () {
      return Reflect.construct(HTMLElement, [], FindYourSize);
    };

    FindYourSize.prototype = Object.create(HTMLElement.prototype);
    FindYourSize.prototype.constructor = FindYourSize;
    Object.setPrototypeOf(FindYourSize, HTMLElement);

    FindYourSize.prototype.connectedCallback = function () {
      if (!this.hasInitialised) {
        this.hasInitialised = true;
        this.setup();
      }

      this.updateTrigger();
    };

    FindYourSize.prototype.setup = function () {
      var self = this;
      var dataElement = this.querySelector('script[data-fys-data]');

      this.data = { sizes: [], fit: 'true_to_size', productUrl: '' };

      if (dataElement) {
        try {
          this.data = JSON.parse(dataElement.textContent);
        } catch (error) {
          /* Without valid data the adviser stays closed. */
        }
      }

      this.sizes = this.data.sizes || [];
      this.state = readState();

      this.dialog = this.querySelector('.custom-fys__dialog');
      this.stepElements = Array.prototype.slice.call(this.querySelectorAll('[data-fys-step]'));
      this.stepKeys = this.stepElements.map(function (element) {
        return element.getAttribute('data-fys-step');
      });

      this.backArrow = this.querySelector('[data-fys-back-arrow]');
      this.brandSelect = this.querySelector('[data-fys-brand-size]');
      this.basicNext = this.querySelector('[data-fys-step="basic"] [data-fys-next]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-fys-choice]'));
      this.unitButtons = Array.prototype.slice.call(this.querySelectorAll('[data-fys-unit]'));
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-fys-input]'));

      this.headingElement = this.querySelector('[data-fys-heading]');
      this.sizeElement = this.querySelector('[data-fys-size]');
      this.statusElement = this.querySelector('[data-fys-status]');
      this.statusTextElement = this.querySelector('[data-fys-status-text]');
      this.noteElement = this.querySelector('[data-fys-note]');
      this.availableElement = this.querySelector('[data-fys-available]');
      this.unavailableElement = this.querySelector('[data-fys-unavailable]');
      this.variantInput = this.querySelector('[data-fys-variant-input]');
      this.addButton = this.querySelector('[data-fys-add]');
      this.addTextElement = this.querySelector('[data-fys-add-text]');
      this.similarWrapper = this.querySelector('[data-fys-similar]');
      this.similarEmpty = this.querySelector('[data-fys-similar-empty]');
      this.similarItems = Array.prototype.slice.call(this.querySelectorAll('.custom-fys__similar-item'));
      this.fitButtons = Array.prototype.slice.call(this.querySelectorAll('[data-fys-fit]'));

      this.resultHeading = this.getAttribute('data-fys-result-heading') || '';
      this.unavailableHeading = this.getAttribute('data-fys-unavailable-heading') || '';
      this.perfectText = this.getAttribute('data-fys-perfect-text') || '';
      this.triggerText = this.getAttribute('data-custom-fys-trigger-text') || '';
      this.triggerCompletedText = this.getAttribute('data-fys-trigger-completed-text') || '';
      this.addTemplate = this.getAttribute('data-fys-add-template') || '';
      this.noteTemplate = this.getAttribute('data-fys-note-template') || '';

      /* Continue and Find my ideal size */
      Array.prototype.forEach.call(this.querySelectorAll('[data-fys-next]'), function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.goToStep(self.currentStep + 1);
        });
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-fys-back-arrow]'), function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.goToStep(self.currentStep - 1);
        });
      });

      var startOverButton = this.querySelector('[data-fys-start-over]');

      if (startOverButton) {
        startOverButton.addEventListener('click', function (event) {
          event.preventDefault();
          self.startOver();
        });
      }

      if (this.brandSelect) {
        this.brandSelect.addEventListener('change', function () {
          self.state.brandSize = self.brandSelect.value;
          self.brandSelect.classList.toggle('is-placeholder', !self.brandSelect.value);
          self.saveState();
          self.validateBasic();
        });
      }

      this.inputs.forEach(function (input) {
        input.addEventListener('input', function () {
          self.state[input.getAttribute('data-fys-input')] = input.value;
          self.saveState();
        });
      });

      this.unitButtons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.state[button.getAttribute('data-fys-unit') + 'Unit'] = button.getAttribute('data-fys-value');
          self.saveState();
          self.syncUnits();
        });
      });

      this.cards.forEach(function (card) {
        card.addEventListener('click', function (event) {
          event.preventDefault();
          var group = card.getAttribute('data-fys-choice');
          self.state[group] = card.getAttribute('data-fys-value');
          self.saveState();
          self.syncCards();
        });
      });

      this.fitButtons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.state.fit = button.getAttribute('data-fys-fit');
          self.saveState();
          self.renderResult();
        });
      });

      this.applyState();
      this.currentStep = 0;
      this.goToStep(0);
    };

    FindYourSize.prototype.saveState = function () {
      writeState(this.state);
    };

    FindYourSize.prototype.applyState = function () {
      var self = this;

      this.inputs.forEach(function (input) {
        var key = input.getAttribute('data-fys-input');

        if (self.state[key]) {
          input.value = self.state[key];
        }
      });

      if (this.brandSelect) {
        var hasSize = false;

        Array.prototype.forEach.call(this.brandSelect.options, function (option) {
          if (option.value && option.value === self.state.brandSize) {
            hasSize = true;
          }
        });

        this.brandSelect.value = hasSize ? this.state.brandSize : '';
        this.brandSelect.classList.toggle('is-placeholder', !this.brandSelect.value);
      }

      this.syncUnits();
      this.syncCards();
      this.validateBasic();
    };

    FindYourSize.prototype.syncUnits = function () {
      var self = this;
      var seen = {};

      this.unitButtons.forEach(function (button) {
        var group = button.getAttribute('data-fys-unit');
        var value = button.getAttribute('data-fys-value');

        if (!self.state[group + 'Unit'] && !seen[group]) {
          self.state[group + 'Unit'] = value;
        }

        seen[group] = true;
      });

      this.unitButtons.forEach(function (button) {
        var group = button.getAttribute('data-fys-unit');
        var active = self.state[group + 'Unit'] === button.getAttribute('data-fys-value');

        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    FindYourSize.prototype.syncCards = function () {
      var self = this;

      this.cards.forEach(function (card) {
        var group = card.getAttribute('data-fys-choice');
        var active = self.state[group] === card.getAttribute('data-fys-value');

        card.classList.toggle('is-active', active);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    FindYourSize.prototype.validateBasic = function () {
      if (this.basicNext) {
        this.basicNext.disabled = !(this.brandSelect && this.brandSelect.value);
      }
    };

    FindYourSize.prototype.goToStep = function (target) {
      var index = typeof target === 'number' ? target : this.stepKeys.indexOf(target);
      var lastIndex = this.stepElements.length - 1;

      index = clamp(index, lastIndex);
      this.currentStep = index;

      this.stepElements.forEach(function (element, position) {
        element.hidden = position !== index;
      });

      if (this.backArrow) {
        this.backArrow.hidden = index === 0;
      }

      if (this.stepKeys[index] === 'result') {
        this.state.completed = true;
        this.saveState();
        this.renderResult();
      }

      if (this.dialog) {
        this.dialog.scrollTop = 0;
      }
    };

    FindYourSize.prototype.openFlow = function () {
      if (this.state.completed && this.state.brandSize) {
        this.goToStep('result');
      } else {
        this.goToStep(0);
      }
    };

    FindYourSize.prototype.startOver = function () {
      var self = this;

      this.state = defaultState();
      this.saveState();

      this.inputs.forEach(function (input) {
        input.value = '';
      });

      if (this.brandSelect) {
        this.brandSelect.value = '';
      }

      this.applyState();
      this.updateTrigger();
      this.goToStep(0);

      window.setTimeout(function () {
        if (self.brandSelect) {
          self.brandSelect.classList.add('is-placeholder');
        }
      }, 0);
    };

    FindYourSize.prototype.baseIndex = function () {
      var chosen = this.state.brandSize;
      var i;

      for (i = 0; i < this.sizes.length; i++) {
        if (this.sizes[i].name === chosen) {
          return i;
        }
      }

      /* The customer wears a size this product does not carry, so we take the closest one. */
      var wanted = canonicalIndex(chosen);

      if (wanted >= 0) {
        var bestIndex = -1;
        var bestDistance = 99;

        for (i = 0; i < this.sizes.length; i++) {
          var current = canonicalIndex(this.sizes[i].name);

          if (current >= 0) {
            var distance = Math.abs(current - wanted);

            if (distance < bestDistance) {
              bestDistance = distance;
              bestIndex = i;
            }
          }
        }

        if (bestIndex >= 0) {
          return bestIndex;
        }
      }

      return Math.floor(this.sizes.length / 2);
    };

    FindYourSize.prototype.recommendedIndex = function () {
      var lastIndex = this.sizes.length - 1;
      var offset = 0;

      if (this.data.fit === 'runs_small') {
        offset = 1;
      } else if (this.data.fit === 'runs_large') {
        offset = -1;
      }

      var idealIndex = clamp(this.baseIndex() + offset, lastIndex);

      if (this.state.fit === 'slimmer') {
        return clamp(idealIndex - 1, lastIndex);
      }

      if (this.state.fit === 'looser') {
        return clamp(idealIndex + 1, lastIndex);
      }

      return idealIndex;
    };

    FindYourSize.prototype.renderResult = function () {
      var self = this;

      if (!this.sizes.length) {
        return;
      }

      var size = this.sizes[this.recommendedIndex()];

      this.fitButtons.forEach(function (button) {
        var active = button.getAttribute('data-fys-fit') === self.state.fit;

        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (this.sizeElement) {
        this.sizeElement.textContent = size.name;
      }

      if (this.noteElement) {
        this.noteElement.textContent = this.noteTemplate.split('[size]').join(size.name);
      }

      if (size.available) {
        if (this.headingElement) {
          this.headingElement.textContent = this.resultHeading;
        }

        if (this.statusElement) {
          this.statusElement.hidden = false;
        }

        if (this.statusTextElement) {
          this.statusTextElement.textContent = this.perfectText;
        }

        if (this.availableElement) {
          this.availableElement.hidden = false;
        }

        if (this.unavailableElement) {
          this.unavailableElement.hidden = true;
        }

        if (this.variantInput) {
          this.variantInput.value = size.variantId || '';
        }

        if (this.addButton) {
          this.addButton.disabled = !size.variantId;
        }

        if (this.addTextElement) {
          this.addTextElement.textContent = this.addTemplate.split('[size]').join(size.name);
        }
      } else {
        if (this.headingElement) {
          this.headingElement.textContent = this.unavailableHeading;
        }

        if (this.statusElement) {
          this.statusElement.hidden = true;
        }

        if (this.availableElement) {
          this.availableElement.hidden = true;
        }

        if (this.unavailableElement) {
          this.unavailableElement.hidden = false;
        }

        this.filterSimilar(size.name);
      }

      this.updateTrigger();
    };

    FindYourSize.prototype.filterSimilar = function (sizeName) {
      var needle = ',' + sizeName + ',';
      var visible = 0;

      this.similarItems.forEach(function (item) {
        var sizes = item.getAttribute('data-fys-sizes') || '';
        var matches = sizes.indexOf(needle) !== -1;

        item.hidden = !matches;

        if (matches) {
          visible += 1;
        }
      });

      if (this.similarWrapper) {
        this.similarWrapper.hidden = visible === 0;
      }

      if (this.similarEmpty) {
        this.similarEmpty.hidden = visible !== 0;
      }
    };

    FindYourSize.prototype.updateTrigger = function () {
      var label = this.triggerText;

      if (this.state && this.state.completed && this.state.brandSize && this.sizes.length) {
        var size = this.sizes[this.recommendedIndex()];

        if (size && this.triggerCompletedText) {
          label = this.triggerCompletedText.split('[size]').join(size.name);
        }
      }

      var triggers = document.querySelectorAll('[data-custom-fys-open="' + this.id + '"]');

      Array.prototype.forEach.call(triggers, function (trigger) {
        if (label) {
          trigger.textContent = label;
        }

        trigger.hidden = false;
      });
    };

    window.customElements.define('find-your-size', FindYourSize);
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) {
      return;
    }

    var trigger = event.target.closest('[data-custom-fys-open]');

    if (trigger) {
      var modal = document.getElementById(trigger.getAttribute('data-custom-fys-open'));

      if (modal) {
        event.preventDefault();

        if (typeof modal.openFlow === 'function') {
          modal.openFlow();
        }

        showModal(modal);
      }

      return;
    }

    if (event.target.closest('[data-custom-fys-close]')) {
      event.preventDefault();
      hideModal();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      hideModal();
    }
  });

  /* The variant picker is re-rendered on every variant change, which brings back a fresh, hidden trigger. */

  function watchForNewTriggers() {
    if (typeof window.MutationObserver !== 'function') {
      return;
    }

    var section = document.querySelector('.shopify-section--main-product');

    if (!section) {
      return;
    }

    var scheduled = false;

    new window.MutationObserver(function () {
      if (scheduled) {
        return;
      }

      scheduled = true;

      window.requestAnimationFrame(function () {
        scheduled = false;

        var modals = document.querySelectorAll('find-your-size');

        Array.prototype.forEach.call(modals, function (modal) {
          if (typeof modal.updateTrigger === 'function') {
            modal.updateTrigger();
          }
        });

        if (openModal && !openModal.isConnected) {
          openModal = null;
          document.documentElement.style.overflow = previousOverflow;
        }
      });
    }).observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForNewTriggers);
  } else {
    watchForNewTriggers();
  }
})();
